// @greenlight-spec: 48-user-passthrough.md#test-sign-in
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { Logo } from '@/components/greenlight/Logo';
import {
  SIGN_IN_TEST_CHANNEL,
  hintsFor,
  signInTestFailure,
  type SignInTestMessage,
  type SignInTestOutcome,
} from '@/components/integrations/signInTestModel';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';

const OUTCOMES: ReadonlySet<string> = new Set(['verified', 'failed', 'refused', 'declined']);

/**
 * Where a Test sign-in ends (`/connect/result?test=1`): the account Greenlight
 * matched, or the cause and fix for the IT admin who ran it. It tells the
 * dashboard tab that opened it over a same-origin BroadcastChannel. Like the
 * end-user result page, nothing from the query string becomes a link as-is.
 */
export function SignInTestResultPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const raw = params.get('outcome') ?? 'failed';
  const outcome = (OUTCOMES.has(raw) ? raw : 'failed') as SignInTestOutcome;
  const integration = params.get('integration') ?? 'this integration';
  const integrationId = params.get('integration_id');
  const credential = params.get('credential');
  const account = params.get('account');
  const reason = params.get('reason');
  const code = params.get('code');
  const retry =
    integrationId && credential
      ? `/connect/start?integration=${encodeURIComponent(integrationId)}&credential=${encodeURIComponent(credential)}&test=1`
      : null;
  const failure =
    outcome === 'verified'
      ? null
      : signInTestFailure(
          { outcome, reason, code },
          hintsFor({ catalogKey: params.get('catalog'), authorizeUrl: null }),
        );

  React.useEffect(() => {
    document.title = 'Test sign-in · Greenlight';
    return () => {
      document.title = 'Greenlight';
    };
  }, []);

  React.useEffect(() => {
    if (!integrationId || !credential || typeof BroadcastChannel === 'undefined') return;
    const message: SignInTestMessage = {
      integrationId,
      credentialSlug: credential,
      outcome,
      account,
      reason,
      code,
    };
    const channel = new BroadcastChannel(SIGN_IN_TEST_CHANNEL);
    channel.postMessage(message);
    channel.close();
  }, [integrationId, credential, outcome, account, reason, code]);

  return (
    <div
      className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-x-hidden bg-background px-5 py-12"
      data-testid="sign-in-test-result"
      data-outcome={outcome}
    >
      <div
        aria-hidden
        className="pointer-events-none fixed -top-[200px] left-1/2 h-[600px] w-[900px] -translate-x-1/2 bg-[radial-gradient(circle,hsl(var(--signal)/0.14)_0%,transparent_62%)]"
      />
      <div className="fixed right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      <main className="relative z-[1] flex w-full max-w-[440px] flex-col items-center">
        <div className="mb-7 flex flex-col items-center gap-2">
          <Logo className="h-7 text-foreground" />
          <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
            by Shift AI
          </div>
        </div>

        <div className="w-full rounded-gl-lg border border-border bg-card p-6 shadow-xl sm:p-8">
          <div className="text-center">
            <div className="mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-muted">
              {failure === null ? (
                <CheckCircle2 className="size-6 text-[hsl(var(--status-green-text))]" aria-hidden />
              ) : (
                <AlertTriangle className="size-6 text-muted-foreground" aria-hidden />
              )}
            </div>
            <Eyebrow>Test sign-in · {integration}</Eyebrow>
            <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">
              {failure === null ? 'Sign-in works' : 'Test sign-in failed'}
            </h1>
          </div>
          {failure === null ? (
            <p className="mt-2 text-center text-sm leading-relaxed text-muted-foreground">
              You are connected as yourself, the way a person using an app with this integration
              would be. Close this tab; the dashboard shows the result.
            </p>
          ) : (
            <div className="mt-4 space-y-2 text-sm leading-relaxed">
              {failure.code && (
                <p className="font-mono text-[12.5px] text-muted-foreground">{failure.code}</p>
              )}
              <p className="text-foreground">{failure.cause}</p>
              <p className="text-foreground">
                <span className="font-medium">Fix: </span>
                <span className="wrap-anywhere">{failure.fix}</span>
              </p>
            </div>
          )}
          {account && (
            <p className="mt-3 text-center text-sm text-muted-foreground">
              {failure === null ? 'Matched account: ' : 'Signed in as: '}
              <span className="font-mono text-[12.5px] text-foreground wrap-anywhere">
                {account}
              </span>
            </p>
          )}
          <div className="mt-6 flex flex-col gap-2">
            {failure !== null && retry && (
              <Button asChild className="h-11 w-full">
                <a href={retry}>Test again</a>
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              className="h-11 w-full"
              onClick={() => window.close()}
            >
              Close tab
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
